var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { getRefreshToken, invalidateTokens, useAuth, useDispatch, useIntegratorLoginMutation, useTokenRefreshMutation, } from "@madmedical/store";
import { useNavigate } from "@madmedical/routing";
import { useIsCurrentRouteNha, useIsCurrentRouteOpen, } from "@madmedical/config";
import { setUser as setSentryUser } from "@madmedical/error";
const AuthWrapper = ({ config: { integrator, integratorToken, integratorAuthLoading }, children, }) => {
    const [isFetching, setFetching] = useState(false);
    const [tokenRefresh] = useTokenRefreshMutation();
    const [integratorLogin] = useIntegratorLoginMutation();
    const { isLoading, isTokenAvailable, userId } = useAuth();
    const isOpenRoute = useIsCurrentRouteOpen();
    const isNhaRoute = useIsCurrentRouteNha();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const logout = useCallback(() => {
        dispatch(invalidateTokens());
        if (!integrator && !isOpenRoute) {
            navigate("/login");
        }
    }, [dispatch, integrator, isOpenRoute, navigate]);
    // Fetch auth Token when missing from store
    // - If integrator token is gone, log out
    // - When refresh token is in storage, use that
    // - If we are in SDK context, log in via integrator
    // - Redirect to login otherwise
    // Happens at page refresh for example.
    useEffect(() => {
        if (isFetching) {
            return;
        }
        if (integrator && integratorAuthLoading) {
            return;
        }
        if (integrator && !integratorToken) {
            // Log out if integrator token is gone for good
            logout();
            return;
        }
        if (isTokenAvailable) {
            // Already have an access token
            return;
        }
        const refreshOrLogin = () => __awaiter(void 0, void 0, void 0, function* () {
            const refreshToken = yield getRefreshToken();
            if (refreshToken) {
                // Just refresh
                try {
                    yield tokenRefresh(refreshToken).unwrap();
                    return;
                }
                catch (_b) {
                    console.error("Token refresh failed. Moving on.");
                }
            }
            if (integrator && integratorToken) {
                // Fresh login via integrator
                yield integratorLogin({
                    integrator,
                    token: integratorToken,
                }).unwrap();
                return;
            }
            // Back to base
            logout();
        });
        try {
            setFetching(true);
            void refreshOrLogin();
        }
        catch (_a) {
            logout();
        }
        finally {
            setFetching(false);
        }
    }, [
        isFetching,
        isTokenAvailable,
        integrator,
        integratorToken,
        integratorAuthLoading,
        logout,
        tokenRefresh,
        integratorLogin,
    ]);
    useEffect(() => {
        setSentryUser(userId
            ? {
                id: userId,
                ip_address: "{{auto}}",
            }
            : null);
    }, [userId]);
    if (isLoading && isNhaRoute && !isOpenRoute) {
        return null;
    }
    return _jsx(_Fragment, { children: children });
};
export default AuthWrapper;
